// Call Lists — PropertyRadar-style tile UI.
// Features:
//  • Active vs Archived tabs
//  • Skip-Trace completion badge (per list)
//  • Call-Progress % badge (dialed / with_phone)
//  • Admin-only color tags: Distress / Absentee / Preforeclosure / Vacant /
//    Probate / Tired LL / High Equity / Inheritance
//  • Admin-only Rename / Delete / Archive
//  • CSV import lives HERE (not on the CRM board)
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ListChecks, Loader2, PhoneCall, Pencil, Trash2, Plus, Search,
  ChevronRight, LayoutGrid, Zap, Settings, Flame, CheckCircle2,
  AlertCircle, Fingerprint, Archive, ArchiveRestore, Tag,
} from "lucide-react";
import ImportLeadsDialog from "@/components/ImportLeadsDialog";

const TAG_LIBRARY = [
  { key: "distress",       label: "Distress",        bg: "bg-red-100 text-red-800 border-red-200" },
  { key: "absentee",       label: "Absentee",        bg: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "preforeclosure", label: "Preforeclosure",  bg: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "vacant",         label: "Vacant",          bg: "bg-slate-100 text-slate-800 border-slate-200" },
  { key: "probate",        label: "Probate",         bg: "bg-purple-100 text-purple-800 border-purple-200" },
  { key: "tired_landlord", label: "Tired LL",        bg: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "high_equity",    label: "High Equity",     bg: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "inheritance",    label: "Inheritance",     bg: "bg-indigo-100 text-indigo-800 border-indigo-200" },
];

function tagMeta(key) {
  return TAG_LIBRARY.find((t) => t.key === key) || null;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function LeadLists() {
  const { user, role } = useAuth();
  const isAdmin = role === "admin" || user?.role === "admin" || user?.is_admin;
  const [activeTab, setActiveTab] = useState("active"); // "active" | "archived"
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("");
  const [boards, setBoards] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [tagOpenFor, setTagOpenFor] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const qs = activeTab === "archived" ? "?only_archived=true" : "";
      const [{ data: listsRes }, { data: boardsRes }] = await Promise.all([
        api.get(`/leads/lists${qs}`),
        api.get("/boards"),
      ]);
      setLists(listsRes.lists || []);
      setBoards(boardsRes.boards || boardsRes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeTab]);

  const renameList = async (oldName) => {
    if (!isAdmin) return toast.error("Only admins can rename lists");
    const to = window.prompt(`Rename "${oldName}" to:`, oldName);
    if (!to || to.trim() === "" || to === oldName) return;
    try {
      const { data } = await api.post("/leads/lists/rename",
        { from: oldName, to: to.trim() });
      toast.success(`Renamed ${data.modified} leads`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const deleteList = async (name, total) => {
    if (!isAdmin) return toast.error("Only admins can delete lists");
    if (!window.confirm(
      `Delete "${name}" — permanently remove ${total.toLocaleString()} lead${total === 1 ? "" : "s"}?`
    )) return;
    const conf = window.prompt(`Type YES to confirm deleting ${total} leads:`);
    if (conf !== "YES") return toast.info("Cancelled");
    try {
      const { data } = await api.post("/leads/lists/delete",
        { name, confirm: true });
      toast.success(`Deleted ${data.deleted} leads`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const archiveList = async (name, archived) => {
    if (!isAdmin) return toast.error("Only admins can archive lists");
    try {
      const { data } = await api.post("/leads/lists/archive",
        { name, archived });
      toast.success(archived
        ? `Archived ${data.modified} leads`
        : `Restored ${data.modified} leads`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  const tagList = async (name, tag) => {
    if (!isAdmin) return toast.error("Only admins can tag lists");
    try {
      const { data } = await api.post("/leads/lists/tag", { name, tag });
      toast.success(tag ? `Tagged ${data.modified} leads · ${tagMeta(tag)?.label || tag}` : "Tag cleared");
      setTagOpenFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tag failed");
    }
  };

  const dialList       = (name) => navigate(`/dialer?list=${encodeURIComponent(name)}`);

  // Skip-trace ONLY the leads in this list that are missing contact info
  // (no phone). Removes any lead that fails skip-trace from the list so
  // the tile stays clean. Idempotent — safe to click repeatedly; already-
  // enriched leads are ignored server-side (query filter).
  const [skipBusy, setSkipBusy] = useState({});
  const skipTraceList = async (name) => {
    if (skipBusy[name]) return;
    setSkipBusy((s) => ({ ...s, [name]: true }));
    try {
      const { data: prev } = await api.get(
        `/leads/bulk-skip-trace-missing/preview`,
        { params: { list_name: name } },
      );
      if (!prev.total_missing) {
        toast.info(`Every lead in "${name}" already has contact info — nothing to skip-trace.`);
        return;
      }
      toast.info(
        `Skip-tracing ${Math.min(prev.total_missing, 200)} lead${prev.total_missing === 1 ? "" : "s"} in "${name}"…`,
      );
      const { data: res } = await api.post("/leads/bulk-skip-trace-missing", {
        list_name: name,
        limit: 200,
        remove_failed_from_list: true,
      });
      const parts = [`Found ${res.found}`];
      if (res.removed_from_list) parts.push(`removed ${res.removed_from_list} un-traceable`);
      if (res.remaining)         parts.push(`${res.remaining} still queued — click Skip Trace again`);
      toast.success(parts.join(" · "));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip-trace failed");
    } finally {
      setSkipBusy((s) => ({ ...s, [name]: false }));
      load();
    }
  };

  const visible = useMemo(() => (
    filter.trim()
      ? lists.filter((l) => l.name.toLowerCase().includes(filter.toLowerCase()))
      : lists
  ), [lists, filter]);

  const grandTotal = lists.reduce((acc, l) => acc + (l.total || 0), 0);

  return (
    <div className="px-6 md:px-10 pb-16 pt-6 md:pt-10 max-w-[1400px] mx-auto"
         data-testid="lead-lists-page">
      <span className="app-eyebrow">/ Call Lists</span>

      <div className="flex items-center justify-between mt-3 mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
            <ListChecks size={32} />
            My Lists
          </h1>
          <p className="text-muted-foreground text-sm max-w-2xl mt-2">
            Upload a CSV, tag it, and start dialing. Every card shows skip-trace
            coverage and how much of the list your team has already worked.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {grandTotal > 0 && (
            <div className="text-right" data-testid="lead-lists-grand-total">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {activeTab === "archived" ? "Archived leads" : "Active leads"}
              </div>
              <div className="font-display text-3xl leading-none">
                {grandTotal.toLocaleString()}
              </div>
            </div>
          )}
          <button
            onClick={() => setImportOpen(true)}
            className="app-btn app-btn-primary text-sm"
            data-testid="lead-lists-new-import"
          >
            <Plus size={13} /> New List
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--app-line)] flex items-center gap-6 mb-6">
        {[
          { key: "active",   label: "Active",   testid: "tab-active" },
          { key: "archived", label: "Archived", testid: "tab-archived" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`lead-lists-${t.testid}`}
            className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {lists.length > 0 && (
        <div className="relative max-w-md mb-6">
          <Search size={13} className="absolute left-3 top-2.5 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search lists…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--app-line)] rounded-full focus:outline-none focus:ring-1 focus:ring-[var(--app-accent)]"
            data-testid="lead-lists-search"
          />
        </div>
      )}

      {busy && (
        <div className="mt-16 text-center text-muted-foreground">
          <Loader2 className="animate-spin mx-auto mb-2" size={20} />
          Loading your lists…
        </div>
      )}

      {!busy && lists.length === 0 && activeTab === "active" && (
        <div className="mt-10 card-flat p-10 text-center" data-testid="lead-lists-empty">
          <ListChecks size={44} className="mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-medium">No lists yet</div>
          <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Import a CSV, name it something like &quot;Cape Coral Absentee 6k&quot; —
            it&apos;ll show up here as a callable bucket.
          </div>
          <button
            onClick={() => setImportOpen(true)}
            className="app-btn app-btn-primary mt-4 text-sm inline-flex"
            data-testid="lead-lists-empty-import"
          >
            Import your first list <ChevronRight size={13} />
          </button>
        </div>
      )}

      {!busy && lists.length === 0 && activeTab === "archived" && (
        <div className="mt-10 card-flat p-10 text-center"
             data-testid="lead-lists-empty-archived">
          <Archive size={44} className="mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-medium">No archived lists</div>
          <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            When a list is fully worked, archive it from the gear menu — it&apos;ll
            move here and stay out of the dialer.
          </div>
        </div>
      )}

      {!busy && visible.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
             data-testid="lead-lists-grid">
          {visible.map((l) => (
            <ListTile
              key={l.name}
              list={l}
              isAdmin={isAdmin}
              menuOpen={menuOpenFor === l.name}
              tagOpen={tagOpenFor === l.name}
              onMenuToggle={() => {
                setTagOpenFor(null);
                setMenuOpenFor(menuOpenFor === l.name ? null : l.name);
              }}
              onTagToggle={() => {
                setMenuOpenFor(null);
                setTagOpenFor(tagOpenFor === l.name ? null : l.name);
              }}
              onCloseMenu={() => setMenuOpenFor(null)}
              onCloseTag={() => setTagOpenFor(null)}
              onDial={() => dialList(l.name)}
              onSkipTrace={() => skipTraceList(l.name)}
              skipBusy={!!skipBusy[l.name]}
              onRename={() => { setMenuOpenFor(null); renameList(l.name); }}
              onDelete={() => { setMenuOpenFor(null); deleteList(l.name, l.total); }}
              onArchive={() => { setMenuOpenFor(null); archiveList(l.name, !l.list_archived); }}
              onSetTag={(tagKey) => tagList(l.name, tagKey)}
              onBoard={() => navigate(`/boards?list=${encodeURIComponent(l.name)}`)}
            />
          ))}
        </div>
      )}

      <ImportLeadsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        boards={boards}
        simple
        onImported={() => { setImportOpen(false); load(); }}
      />
    </div>
  );
}

function ListTile({ list, isAdmin, menuOpen, tagOpen, onMenuToggle, onTagToggle,
                    onCloseMenu, onCloseTag, onDial, onSkipTrace, skipBusy, onRename,
                    onDelete, onArchive, onSetTag, onBoard }) {
  const l = list;
  const pctSkip = l.skip_trace_pct ?? 0;
  const pctCall = l.call_progress_pct ?? 0;
  const isSkipComplete = pctSkip >= 100;
  const isSkipPartial  = pctSkip > 0 && pctSkip < 100;
  const isSkipEmpty    = pctSkip === 0;
  const tag = tagMeta(l.list_tag);

  return (
    <div
      className="rounded-2xl border border-[var(--app-line)] bg-white p-5 hover:shadow-md transition-shadow relative"
      data-testid={`lead-list-card-${l.name}`}
      onMouseLeave={() => { onCloseMenu(); onCloseTag(); }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Zap size={16} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-semibold text-base truncate leading-tight" title={l.name}>
              {l.name}
            </h3>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Added {timeAgo(l.last_added)}
              {l.list_archived && (
                <span className="ml-2 inline-flex items-center gap-1 text-slate-500">
                  · <Archive size={9} /> Archived
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="relative shrink-0">
          <button
            onClick={onMenuToggle}
            title="List settings"
            data-testid={`lead-list-menu-${l.name}`}
            className="p-1.5 rounded-full hover:bg-slate-100 text-muted-foreground hover:text-slate-900"
          >
            <Settings size={14} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-8 z-20 w-44 rounded-xl border border-[var(--app-line)] bg-white shadow-lg py-1 text-sm"
              data-testid={`lead-list-menu-panel-${l.name}`}
            >
              <button
                onClick={onRename}
                disabled={!isAdmin}
                data-testid={`lead-list-rename-${l.name}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Pencil size={12} /> Rename
                {!isAdmin && <span className="ml-auto text-[9px] uppercase text-slate-400">Admin</span>}
              </button>
              <button
                onClick={onArchive}
                disabled={!isAdmin}
                data-testid={`lead-list-archive-${l.name}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {l.list_archived
                  ? <><ArchiveRestore size={12} /> Restore</>
                  : <><Archive size={12} /> Archive</>}
                {!isAdmin && <span className="ml-auto text-[9px] uppercase text-slate-400">Admin</span>}
              </button>
              <button
                onClick={onDelete}
                disabled={!isAdmin}
                data-testid={`lead-list-delete-${l.name}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 size={12} /> Delete
                {!isAdmin && <span className="ml-auto text-[9px] uppercase text-slate-400">Admin</span>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tag pill (or picker if admin) */}
      <div className="mt-3 relative">
        {tag ? (
          <button
            type="button"
            onClick={isAdmin ? onTagToggle : undefined}
            disabled={!isAdmin}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${tag.bg} ${isAdmin ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
            data-testid={`lead-list-tag-${l.name}`}
            title={isAdmin ? "Change tag" : "Tag"}
          >
            <Tag size={10} /> {tag.label}
          </button>
        ) : isAdmin ? (
          <button
            type="button"
            onClick={onTagToggle}
            data-testid={`lead-list-add-tag-${l.name}`}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50"
          >
            <Tag size={10} /> Add tag
          </button>
        ) : (
          <span className="text-[10px] text-slate-400">No tag</span>
        )}

        {tagOpen && isAdmin && (
          <div
            className="absolute left-0 top-8 z-30 w-56 rounded-xl border border-[var(--app-line)] bg-white shadow-lg p-2"
            data-testid={`lead-list-tag-picker-${l.name}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500 px-1 pb-1">
              Choose a tag
            </div>
            <div className="grid grid-cols-2 gap-1">
              {TAG_LIBRARY.map((t) => (
                <button
                  key={t.key}
                  onClick={() => onSetTag(t.key)}
                  data-testid={`tag-option-${t.key}`}
                  className={`text-[11px] px-2 py-1 rounded-full border ${t.bg} hover:opacity-80`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {l.list_tag && (
              <button
                onClick={() => onSetTag(null)}
                data-testid={`tag-clear-${l.name}`}
                className="mt-2 w-full text-[11px] px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Clear tag
              </button>
            )}
          </div>
        )}
      </div>

      {/* Big count */}
      <div className="mt-3">
        <div className="font-display text-4xl leading-none text-sky-600">
          {l.total.toLocaleString()}
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {l.hot > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
                  data-testid={`lead-list-hot-badge-${l.name}`}>
              <Flame size={10} /> {l.hot.toLocaleString()} hot
            </span>
          )}
          {l.connected > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              ● {l.connected.toLocaleString()} connected
            </span>
          )}
          {l.callbacks > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
              {l.callbacks.toLocaleString()} callback
            </span>
          )}
        </div>
      </div>

      {/* Skip-Trace status */}
      <div className="mt-4" data-testid={`lead-list-skip-badge-${l.name}`}>
        {isSkipComplete && (
          <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle2 size={14} className="shrink-0" />
            <div>
              <div className="font-semibold">Skip Trace complete</div>
              <div className="text-[10px] opacity-80">
                All {l.total.toLocaleString()} records have a phone on file
              </div>
            </div>
          </div>
        )}
        {isSkipPartial && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-amber-800">
                <Fingerprint size={14} />
                <div className="font-semibold">Skip Trace {pctSkip}%</div>
              </div>
              <div className="text-[10px] text-amber-700">
                {l.missing_contact.toLocaleString()} missing
              </div>
            </div>
            <div className="mt-1.5 h-1.5 w-full bg-amber-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                   style={{ width: `${pctSkip}%` }} />
            </div>
          </div>
        )}
        {isSkipEmpty && (
          <div className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <AlertCircle size={14} className="shrink-0 text-slate-500" />
            <div>
              <div className="font-semibold">No contact info yet</div>
              <div className="text-[10px] opacity-80">
                Skip-trace this list to unlock dialing
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calling progress */}
      {l.with_phone > 0 && (
        <div className="mt-3" data-testid={`lead-list-call-progress-${l.name}`}>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500">
            <span>Calling progress</span>
            <span className="normal-case tracking-normal text-[11px] text-slate-900 font-medium">
              {pctCall}% · {l.dialed.toLocaleString()} of {l.with_phone.toLocaleString()} dialed
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                pctCall >= 100 ? "bg-emerald-500" :
                pctCall >= 50  ? "bg-sky-500" :
                pctCall >= 15  ? "bg-amber-500" :
                                 "bg-slate-400"
              }`}
              style={{ width: `${Math.min(pctCall, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={onDial}
          disabled={l.with_phone === 0 || l.list_archived}
          className="flex-1 app-btn app-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`lead-list-dial-${l.name}`}
          title={l.list_archived ? "Restore this list to dial" : (l.with_phone === 0 ? "No phones on file — skip trace first" : `Dial ${l.with_phone} numbers`)}
        >
          <PhoneCall size={12} /> Dial
        </button>
        <button
          onClick={onSkipTrace}
          disabled={skipBusy || l.list_archived}
          className="app-btn text-xs border border-[var(--app-line)] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`lead-list-skiptrace-${l.name}`}
          title="Skip-trace only the leads in this list that are missing contact info. Un-traceable leads are removed from the list."
        >
          {skipBusy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Fingerprint size={12} />
          )}
          {skipBusy ? "Tracing…" : "Skip Trace"}
        </button>
        {/* "View on CRM board" button removed 2026-02-17 by owner —
            lists should stay in this page and NOT auto-flow into the
            CRM Pipeline. Reps work the phones straight off the list
            here (Dial + Skip Trace above) so the pipeline board stays
            clean for real deals. */}
      </div>
    </div>
  );
}
